import WebApp from "@twa-dev/sdk";

// Thin wrapper around @twa-dev/sdk so we centralize the integration
// (haptics, MainButton, theme application) and stay testable.
export function initTelegram(): { initData: string; ready: boolean } {
  try {
    WebApp.ready();
    WebApp.expand();
    applyTheme();
    // Telegram fires this when the user switches between light/dark mid-session.
    WebApp.onEvent("themeChanged", applyTheme);
    return { initData: WebApp.initData ?? "", ready: true };
  } catch {
    applyTheme();
    // Running outside Telegram (browser dev). Return empty initData so the
    // auth call fails gracefully and the UI can render a "open in Telegram"
    // placeholder.
    return { initData: "", ready: false };
  }
}

// Reflects Telegram WebApp.colorScheme onto <html data-theme="..."> so
// Tailwind's `dark:` variant (configured via darkMode selector in
// tailwind.config.ts) and our own `[data-theme="..."]` CSS rules can react.
function applyTheme() {
  try {
    const scheme = WebApp.colorScheme === "light" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", scheme);
    document.documentElement.style.colorScheme = scheme;
  } catch {
    // Outside Telegram: default to dark since that's what we design against.
    document.documentElement.setAttribute("data-theme", "dark");
    document.documentElement.style.colorScheme = "dark";
  }
}

export function hapticImpact(style: "light" | "medium" | "heavy" = "light") {
  try {
    WebApp.HapticFeedback.impactOccurred(style);
  } catch {
    // no-op outside Telegram
  }
}

export function hapticNotification(type: "success" | "warning" | "error") {
  try {
    WebApp.HapticFeedback.notificationOccurred(type);
  } catch {
    // no-op outside Telegram
  }
}

// Wraps Telegram WebApp.openInvoice for Stars purchases. Resolves with
// the final status: 'paid', 'cancelled', 'failed', or 'pending'.
// Outside Telegram (dev mode) resolves to 'failed' so the UI can render
// an error rather than hang.
export function openInvoice(url: string): Promise<"paid" | "cancelled" | "failed" | "pending"> {
  return new Promise((resolve) => {
    try {
      WebApp.openInvoice(url, (status) => {
        resolve(status as "paid" | "cancelled" | "failed" | "pending");
      });
    } catch {
      resolve("failed");
    }
  });
}

export function showAlert(message: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      WebApp.showAlert(message, () => resolve());
    } catch {
      window.alert(message);
      resolve();
    }
  });
}

export { WebApp };
